import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { decrypt } from '../utils/security';

export default function AnalyticsPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, t] = await Promise.all([adminAPI.getActivity(), adminAPI.getTopBooks()]);
        setData({ activity: a.data, topBooks: t.data });
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
      <div style={{ width:32, height:32, border:'2px solid #E8E4DF', borderTopColor:'#C97B77', borderRadius:'50%', animation:'spin .8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const daily    = data?.activity?.dailyActivity  || [];
  const types    = data?.activity?.typeBreakdown  || [];
  const topAnnot = data?.topBooks?.topAnnotated   || [];
  const maxCount = Math.max(...daily.map(d => d.count), 1);

  const typeColors = { highlight:'#FBF3D0', note:'#EEF2F8', bookmark:'#F0EDF8' };
  const typeBorderColors = { highlight:'#C4975A', note:'#6B7FA0', bookmark:'#9B8EC4' };

  return (
    <div style={{ padding:32, fontFamily:'Nunito,sans-serif' }}>
      <div style={{ marginBottom:28 }}>
        <div style={{ fontFamily:'Playfair Display,serif', fontSize:26, fontWeight:700, color:'#2C2C35' }}>
          Platform <em style={{ color:'#C97B77', fontStyle:'italic' }}>Analytics</em>
        </div>
        <div style={{ fontSize:13, color:'#888899', marginTop:4 }}>Usage insights and engagement metrics</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:20, marginBottom:20 }}>
        {/* Daily activity bar chart */}
        <div style={{ background:'white', borderRadius:14, border:'1px solid #E8E4DF', padding:24 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#2C2C35', marginBottom:4 }}>📅 Daily Annotation Activity</div>
          <div style={{ fontSize:11, color:'#888899', marginBottom:20 }}>Highlights + Notes created per day (last 7 days)</div>
          {daily.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#888899', fontSize:13 }}>No activity data yet</div>
          ) : (
            <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:160 }}>
              {daily.map((d, i) => {
                const pct = (d.count / maxCount) * 100;
                return (
                  <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#C97B77' }}>{d.count}</div>
                    <div style={{ width:'100%', borderRadius:'4px 4px 0 0', background:'#C97B77', height:`${Math.max(pct, 4)}%`, transition:'height .4s', opacity:.8 }}/>
                    <div style={{ fontSize:9, color:'#888899', textAlign:'center', lineHeight:1.2 }}>
                      {d._id?.split('-').slice(1).join('/')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Type breakdown */}
        <div style={{ background:'white', borderRadius:14, border:'1px solid #E8E4DF', padding:24 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#2C2C35', marginBottom:4 }}>📊 Annotation Types</div>
          <div style={{ fontSize:11, color:'#888899', marginBottom:20 }}>Breakdown by annotation type</div>
          {types.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#888899', fontSize:13 }}>No data yet</div>
          ) : types.map((t, i) => {
            const total = types.reduce((s, x) => s + x.count, 0);
            const pct   = total > 0 ? Math.round((t.count / total) * 100) : 0;
            const bg    = typeColors[t._id]   || '#F5F2EE';
            const bc    = typeBorderColors[t._id] || '#C97B77';
            return (
              <div key={i} style={{ marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'#2C2C35', textTransform:'capitalize' }}>{t._id || 'other'}</span>
                  <span style={{ fontSize:12, fontWeight:700, color: bc }}>{t.count} ({pct}%)</span>
                </div>
                <div style={{ height:10, borderRadius:5, background:'#F5F2EE', overflow:'hidden' }}>
                  <div style={{ height:'100%', borderRadius:5, background: bc, width:`${pct}%`, transition:'width .4s' }}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top annotated books */}
      <div style={{ background:'white', borderRadius:14, border:'1px solid #E8E4DF', padding:24 }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#2C2C35', marginBottom:4 }}>🔥 Most Engaged Books</div>
        <div style={{ fontSize:11, color:'#888899', marginBottom:20 }}>Books with the most highlights and notes</div>
        {topAnnot.length === 0 ? (
          <div style={{ textAlign:'center', padding:40, color:'#888899', fontSize:13 }}>No annotation data yet</div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:12 }}>
            {topAnnot.map((b, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:10, border:'1px solid #E8E4DF', background:'#FAFAF8' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700,
                  background: i < 3 ? '#C97B77' : '#EEF2F8', color: i < 3 ? 'white' : '#6B7FA0', flexShrink:0 }}>
                  {i+1}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#2C2C35', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {decrypt(b.bookName || b.book_title) || `Book #${b._id}`}
                  </div>
                  <div style={{ fontSize:10, color:'#888899', marginTop:2 }}>Book ID: {b._id}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:'#C97B77', flexShrink:0 }}>{b.count}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}